import { BookEngineNav } from '../components/editor/BookEngineNav';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useParams } from '@tanstack/react-router';
import {
  useConsistencyWarnings,
  useDismissWarning,
} from '../api/queries/consistency';
import { useMarkLastEngine } from '../api/queries/bookSession';

export function ConsistencyRoute() {
  const { bookId } = useParams({ strict: false }) as { bookId: string };
  useMarkLastEngine(bookId, 'consistency');
  const warnings = useConsistencyWarnings({ bookId });
  const dismiss = useDismissWarning();

  if (warnings.isLoading) return <CircularProgress />;
  if (warnings.isError)
    return <Alert severity="error">Failed to load consistency warnings.</Alert>;
  const open = warnings.data?.filter((w) => w.status === 'open') ?? [];

  // Group by entity.
  const byEntity = new Map<string, typeof open>();
  for (const w of open) {
    const list = byEntity.get(w.entityId) ?? [];
    list.push(w);
    byEntity.set(w.entityId, list);
  }

  return (
    <div>
      <BookEngineNav bookId={bookId} />
      <Typography variant="h4" sx={{ mb: 2, mt: 2 }}>
        Visual consistency
      </Typography>
      {open.length === 0 && (
        <Alert severity="success">
          No open consistency warnings for this book.
        </Alert>
      )}
      <Stack spacing={2}>
        {Array.from(byEntity.entries()).map(([entityId, list]) => (
          <Card key={entityId}>
            <CardContent>
              <Typography variant="subtitle2">
                {list[0]?.entityName ?? entityId.slice(0, 8)} — {list.length}{' '}
                warning
                {list.length === 1 ? '' : 's'}
              </Typography>
              <Stack spacing={1} sx={{ mt: 1 }}>
                {list.map((w) => (
                  <Stack
                    key={w.id}
                    direction="row"
                    spacing={1}
                    alignItems="center"
                  >
                    <Chip size="small" color="warning" label={w.warningType} />
                    {w.score !== null && w.score !== undefined && (
                      <Typography variant="caption">
                        score {Math.round(w.score * 100)}%
                      </Typography>
                    )}
                    <div style={{ flex: 1 }} />
                    <Button size="small" onClick={() => dismiss.mutate(w.id)}>
                      Dismiss
                    </Button>
                  </Stack>
                ))}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </div>
  );
}
